// src/components/TeenHubView.jsx
//
// The 13–17 ("teen") surface. This cohort sits between KidzCorner (too young)
// and the adult DashboardView (too clinical) — see
// docs/eri-hub-member-funnel-spec.md §5. The brief for this surface: age-
// appropriate brain health, stress/identity content, a *lighter* check-in, and
// peer framing that normalizes rather than medicalizes.
//
// Design notes:
//   - Content is static and self-contained (top-of-file consts, mirroring the
//     PILLARS pattern in BrainHealthView).
//   - The check-in is intentionally REFLECTIVE, not diagnostic: it never scores
//     or labels, it just mirrors back what the teen reports with a supportive
//     nudge. Check-ins persist privately to users/{uid}/private_wellbeing/teen
//     (self-only Firestore rules); without a signed-in uid (e.g. preview mode)
//     the surface stays local-state only. Wiring a real teen assessment to the
//     warehouse is a separate lift.
//
// Routed from resolveSurface() === 'teen' (memberType: child + ageTier: teen).

import React, { useState, useEffect } from 'react';
import {
    Sparkles, Brain, HeartPulse, Users, ChevronDown,
    Moon, Dumbbell, MessageCircle, Compass, ArrowRight,
    LifeBuoy, Phone, MessageSquare, CheckCircle2, Clock,
} from 'lucide-react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase-config';
import SupportOutlet from './dashboard/SupportOutlet';
import { FaceScale, CheckInResults, StreakHeader, checkedInThisWeek, computeStreak } from './shared/CheckInScale';

// --- BRAIN HEALTH, YOUR WAY ---
// The adult Six Pillars, reframed for 13–17. Same science, no lecture.
const TEEN_PILLARS = [
    {
        id: 'sleep',
        icon: Moon,
        title: 'Sleep',
        hook: 'Your brain does its best work while you’re out cold.',
        color: 'text-indigo-600', bg: 'bg-indigo-50',
        body: 'Sleep is when your brain files away everything you learned and clears out the junk. Pulling a late one before a test usually backfires — you remember less, not more.',
        tries: ['Same wake-up time, even on weekends', 'Phone out of the bed', 'Wind down — not scroll down — for the last 30 mins'],
    },
    {
        id: 'move',
        icon: Dumbbell,
        title: 'Move',
        hook: 'Moving your body is a cheat code for your mood.',
        color: 'text-red-600', bg: 'bg-red-50',
        body: 'Exercise pumps blood to your brain and drops stress chemicals. It doesn’t have to be a gym — a walk, a skate, a pickup game all count.',
        tries: ['20 minutes of anything that gets you breathing', 'Move when you’re stuck, not just when you “should”', 'Find a thing you’d do even if no one was watching'],
    },
    {
        id: 'fuel',
        icon: HeartPulse,
        title: 'Fuel',
        hook: 'What you eat shows up in how you think.',
        color: 'text-green-600', bg: 'bg-green-50',
        body: 'Your brain runs on what you feed it. Skipping meals or living on energy drinks makes focus and mood harder than they need to be.',
        tries: ['Eat something real before noon', 'Water before the third coffee', 'You don’t have to be perfect — just fed'],
    },
    {
        id: 'connect',
        icon: Users,
        title: 'Connect',
        hook: 'Real people beat a perfect feed.',
        color: 'text-blue-600', bg: 'bg-blue-50',
        body: 'Feeling connected is one of the biggest things that protects your head long-term. Online counts, but face-to-face hits different.',
        tries: ['Message the person you’ve been meaning to', 'Say yes to one thing this week', 'Quality over quantity — one real friend is plenty'],
    },
    {
        id: 'learn',
        icon: Brain,
        title: 'Learn',
        hook: 'Curiosity literally builds your brain.',
        color: 'text-purple-600', bg: 'bg-purple-50',
        body: 'Learning new things — not just school stuff — builds “reserve” that makes your brain tougher for the rest of your life. Following a rabbit hole is good for you.',
        tries: ['Learn a thing you actually care about', 'Switch up your routine on purpose', 'Bored is allowed — it’s where ideas start'],
    },
    {
        id: 'calm',
        icon: Compass,
        title: 'Calm',
        hook: 'Stress is normal. Staying stuck in it isn’t.',
        color: 'text-pink-600', bg: 'bg-pink-50',
        body: 'Short bursts of stress are fine — that’s your body showing up. The goal isn’t zero stress, it’s knowing how to come back down after.',
        tries: ['Box breathing: in 4, hold 4, out 4, hold 4', 'Name the feeling — it shrinks it', 'Step outside before you spiral'],
    },
];

// --- STRESS & IDENTITY ---
// Meets teens where they are: pressure, comparison, and being more than a result.
const IDENTITY_CARDS = [
    {
        id: 'pressure',
        title: 'The Pressure Is Real',
        body: 'Expectations — from coaches, parents, yourself — are heavy at this age. Naming the pressure out loud takes some of its power away. You’re not weak for feeling it; you’d be unusual not to.',
    },
    {
        id: 'comparison',
        title: 'The Highlight Reel Lies',
        body: 'Everyone online is posting their best 1%. Comparing your behind-the-scenes to someone’s highlight reel is a rigged game. The people you scroll past feel the same way you do.',
    },
    {
        id: 'more-than',
        title: 'You Are More Than the Result',
        body: 'A score, a grade, a tryout — none of them are the whole story of who you are. The stuff that lasts is how you treat people and what you’re curious about, not a number on a page.',
    },
];

// --- YOU ARE NOT ALONE ---
// Peer framing first, real support second. Resources are Canada-correct.
const PEER_VOICES = [
    '“I thought I was the only one overthinking everything. Turns out half my team was too.”',
    '“Asking for help felt huge. It ended up being the most normal thing in the world.”',
    '“Some weeks I’ve got it together, some weeks I don’t. Both are fine, apparently.”',
];

// `detailParts` keeps the exact same copy but marks which pieces are tappable
// (tel:/sms: links) so a teen on a phone can call or text directly.
const SUPPORT_LINES = [
    {
        id: 'kht', icon: MessageSquare, name: 'Kids Help Phone',
        detailParts: [
            { text: 'Text CONNECT to 686868', href: 'sms:686868?body=CONNECT' },
            { text: ' · ' },
            { text: '1-800-668-6868', href: 'tel:1-800-668-6868' },
        ],
        note: 'Free, 24/7, confidential — Canada'
    },
    {
        id: '988', icon: Phone, name: '988 Suicide Crisis Helpline',
        detailParts: [
            { text: 'Call', href: 'tel:988' },
            { text: ' or ' },
            { text: 'text 988', href: 'sms:988' },
        ],
        note: 'If things feel like too much, right now'
    },
];

// --- LIGHTER CHECK-IN (reflective, never scored) ---
// Faces follow each question's own low→high meaning (not a generic sad→happy
// scale) — note stress runs calm→loud, so `higherIsBetter: false`.
const CHECK_IN_PROMPTS = [
    {
        id: 'sleep', label: 'How’s your sleep been lately?', low: 'Rough', high: 'Solid', higherIsBetter: true,
        faces: [
            { emoji: '😫', word: 'Rough' }, { emoji: '😪', word: 'Tired' }, { emoji: '😐', word: 'OK' },
            { emoji: '🙂', word: 'Good' }, { emoji: '😃', word: 'Solid' },
        ],
    },
    {
        id: 'stress', label: 'How loud is the stress this week?', low: 'Quiet', high: 'Loud', higherIsBetter: false,
        faces: [
            { emoji: '😌', word: 'Quiet' }, { emoji: '🙂', word: 'Low' }, { emoji: '😐', word: 'Some' },
            { emoji: '😟', word: 'High' }, { emoji: '😫', word: 'Loud' },
        ],
    },
    {
        id: 'mood', label: 'How are you feeling, mostly?', low: 'Low', high: 'Good', higherIsBetter: true,
        faces: [
            { emoji: '😢', word: 'Low' }, { emoji: '😕', word: 'Meh' }, { emoji: '😐', word: 'OK' },
            { emoji: '🙂', word: 'Good' }, { emoji: '😄', word: 'Great' },
        ],
    },
    {
        id: 'connect', label: 'How connected do you feel to people?', low: 'On my own', high: 'Surrounded', higherIsBetter: true,
        faces: [
            { emoji: '🙁', word: 'Alone' }, { emoji: '😕', word: 'Distant' }, { emoji: '😐', word: 'Some' },
            { emoji: '🙂', word: 'Close' }, { emoji: '🤗', word: 'Surrounded' },
        ],
    },
];

const reflectionFor = (answers) => {
    // No scoring, no labels — just a supportive mirror based on what stands out.
    const stress = answers.stress ?? 3;
    const mood = answers.mood ?? 3;
    const connect = answers.connect ?? 3;
    const sleep = answers.sleep ?? 3;

    if (stress >= 4 && mood <= 2) {
        return 'Sounds like a heavy stretch — high stress and a low mood at the same time is a lot to carry. That’s exactly the kind of week to lean on someone. Scroll down to “You Are Not Alone” if you want a place to start.';
    }
    if (connect <= 2) {
        return 'Feeling a bit on your own right now. That’s more common than it looks — reaching out to one person this week, even just a text, tends to shift it more than you’d expect.';
    }
    if (sleep <= 2) {
        return 'Sleep’s taking a hit, and that quietly makes everything else harder. The “Sleep” card up top has a couple of small things worth trying tonight.';
    }
    if (mood >= 4 && stress <= 2) {
        return 'You’re in a good groove — nice. Worth noticing what’s working so you can come back to it on the harder weeks.';
    }
    return 'Thanks for checking in honestly. There’s no score here and nothing to “pass” — just you, paying attention to how you’re actually doing. That’s the whole point.';
};

// Gentle relative date for the "Last check-in" line — supportive, never a score.
const formatCheckinDate = (iso) => {
    const then = new Date(iso);
    if (isNaN(then.getTime())) return '';
    const days = Math.floor((Date.now() - then.getTime()) / 86400000);
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days < 7) return `${days} days ago`;
    return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

// --- EXPANDABLE PILLAR CARD ---
const PillarCard = ({ pillar }) => {
    const [open, setOpen] = useState(false);
    const Icon = pillar.icon;
    return (
        <div className={`bg-white rounded-2xl border border-slate-200 overflow-hidden transition-shadow ${open ? 'shadow-lg' : 'shadow-sm hover:shadow-md'}`}>
            <button
                onClick={() => setOpen(o => !o)}
                className="w-full p-5 flex items-center gap-4 text-left"
            >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${pillar.bg} ${pillar.color}`}>
                    <Icon size={24} />
                </div>
                <div className="flex-1">
                    <h3 className="text-lg font-black text-slate-900 leading-tight">{pillar.title}</h3>
                    <p className="text-sm text-slate-500">{pillar.hook}</p>
                </div>
                <ChevronDown size={20} className={`text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <div className="px-5 pb-5 pt-1 animate-in slide-in-from-top-2 duration-300">
                    <p className="text-slate-600 leading-relaxed mb-4">{pillar.body}</p>
                    <div className="space-y-2">
                        {pillar.tries.map((t, i) => (
                            <div key={i} className="flex items-start gap-3 text-sm text-slate-700">
                                <ArrowRight size={16} className={`${pillar.color} shrink-0 mt-0.5`} />
                                <span>{t}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const TeenHubView = ({ currentUser }) => {
    const [answers, setAnswers] = useState({});
    const [submitted, setSubmitted] = useState(false);
    // Saved reflective history (private to the teen, never scored).
    const [checkins, setCheckins] = useState([]);

    const allAnswered = CHECK_IN_PROMPTS.every(p => answers[p.id] != null);

    // Load saved check-ins from the private doc (self-only Firestore rules).
    // Without a uid (e.g. preview mode) this surface stays local-only.
    useEffect(() => {
        if (!currentUser?.uid) return;
        let cancelled = false;
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'users', currentUser.uid, 'private_wellbeing', 'teen'));
                if (!cancelled && snap.exists() && Array.isArray(snap.data().checkins)) {
                    const saved = snap.data().checkins;
                    setCheckins(saved);
                    // Already checked in within the last 7 days? Land on the
                    // unlocked reflection (showing that entry), not a blank form.
                    if (checkedInThisWeek(saved)) {
                        const last = saved[saved.length - 1];
                        if (last?.answers) setAnswers(last.answers);
                        setSubmitted(true);
                    }
                }
            } catch (e) {
                console.error('Error loading teen check-ins:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [currentUser?.uid]);

    const handleSubmit = async () => {
        setSubmitted(true);
        const entry = { date: new Date().toISOString(), answers: { ...answers } };
        const next = [...checkins, entry].slice(-30); // keep the most recent 30
        setCheckins(next); // local history always includes this session (results screen reads it)
        if (!currentUser?.uid) return; // preview mode: local-state only, nothing persisted
        try {
            await setDoc(
                doc(db, 'users', currentUser.uid, 'private_wellbeing', 'teen'),
                { checkins: next, updatedAt: new Date().toISOString() },
                { merge: true }
            );
        } catch (e) {
            console.error('Error saving teen check-in:', e);
        }
    };

    const lastCheckin = checkins.length > 0 ? checkins[checkins.length - 1] : null;
    // Weekly pulse framing — derived from history only, never scored.
    const weekDone = checkedInThisWeek(checkins);
    const streak = computeStreak(checkins);

    return (
        <div className="max-w-4xl mx-auto space-y-10 animate-in fade-in pb-20">

            {/* HERO */}
            <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-3xl p-8 text-white relative overflow-hidden shadow-2xl">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
                <div className="relative z-10">
                    <div className="flex items-center gap-2 mb-2 font-bold uppercase tracking-widest text-xs" style={{ color: 'var(--brand-accent)' }}>
                        <Sparkles size={14} /> Teen Hub
                    </div>
                    <h1 className="text-3xl md:text-4xl font-black mb-4">Your space.</h1>
                    <p className="text-slate-300 text-lg leading-relaxed max-w-2xl">
                        Built for 13–17 — not a kids’ arcade, not a clinical dashboard. No scores, no judgement. Just stuff that actually helps.
                    </p>
                    <div className="mt-6">
                        <SupportOutlet currentUser={currentUser} />
                    </div>
                </div>
            </div>

            {/* DASHBOARD STATUS — at-a-glance, no scores */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${weekDone ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>
                        {weekDone ? <CheckCircle2 size={20} /> : <Clock size={20} />}
                    </div>
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">This week</p>
                        <p className="font-black text-slate-800 text-sm">{weekDone ? 'Checked in' : 'Not yet'}</p>
                        <p className="text-[11px] text-slate-400">{streak > 0 ? `${streak} week streak` : 'Start a streak'}</p>
                    </div>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-indigo-50 text-indigo-600"><HeartPulse size={20} /></div>
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Check-ins</p>
                        <p className="font-black text-slate-800 text-sm">{checkins.length} logged</p>
                        <p className="text-[11px] text-slate-400">private to you</p>
                    </div>
                </div>
            </div>

            {/* LIGHTER CHECK-IN */}
            <section>
                <h2 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4 px-1">This week’s pulse</h2>
                <StreakHeader streak={streak} status={weekDone ? 'done' : 'due'} accent="indigo" />
                {lastCheckin && (
                    <p className="text-sm text-slate-500 mb-3 px-1">
                        Last check-in: <span className="font-bold text-slate-600">{formatCheckinDate(lastCheckin.date)}</span>
                        {' · '}{checkins.length} check-in{checkins.length === 1 ? '' : 's'} so far — showing up counts.
                    </p>
                )}
                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 md:p-8">
                    {!submitted ? (
                        <>
                            <p className="text-slate-600 mb-6 leading-relaxed">
                                Do this week’s check-in to unlock your reflection. Four taps, thirty seconds — there’s no right answer and nothing gets graded. It’s just for you.
                            </p>
                            <div className="space-y-8">
                                {CHECK_IN_PROMPTS.map(p => (
                                    <FaceScale
                                        key={p.id}
                                        prompt={p}
                                        value={answers[p.id]}
                                        onChange={(v) => setAnswers(a => ({ ...a, [p.id]: v }))}
                                        accent="indigo"
                                    />
                                ))}
                            </div>
                            <button
                                onClick={handleSubmit}
                                disabled={!allAnswered}
                                className="w-full mt-8 py-4 bg-slate-900 text-white rounded-2xl font-black hover:bg-black transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                See my reflection <ArrowRight size={18} />
                            </button>
                            {!allAnswered && (
                                <p className="text-center text-xs text-slate-400 mt-3">Tap a face for each one to continue.</p>
                            )}
                        </>
                    ) : (
                        <CheckInResults
                            prompts={CHECK_IN_PROMPTS}
                            answers={answers}
                            history={checkins}
                            reflection={reflectionFor(answers)}
                            onStartOver={() => { setSubmitted(false); setAnswers({}); }}
                            accent="indigo"
                        />
                    )}
                </div>
            </section>

            {/* BRAIN HEALTH, YOUR WAY */}
            <section>
                <h2 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1 px-1">Brain health, your way</h2>
                <p className="text-slate-500 text-sm mb-4 px-1">Same science the adults get — minus the lecture. Tap any one to open it.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {TEEN_PILLARS.map(p => <PillarCard key={p.id} pillar={p} />)}
                </div>
            </section>

            {/* STRESS & IDENTITY */}
            <section>
                <h2 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4 px-1">Stress &amp; identity</h2>
                <div className="space-y-4">
                    {IDENTITY_CARDS.map(c => (
                        <div key={c.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                            <h3 className="text-lg font-black text-slate-900 mb-2">{c.title}</h3>
                            <p className="text-slate-600 leading-relaxed">{c.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* YOU ARE NOT ALONE */}
            <section>
                <h2 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4 px-1">You are not alone</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Peer voices */}
                    <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl border border-indigo-100 p-6 space-y-4">
                        <div className="flex items-center gap-2 text-indigo-600 font-black uppercase tracking-widest text-[11px]">
                            <MessageCircle size={14} /> In their words
                        </div>
                        {PEER_VOICES.map((q, i) => (
                            <p key={i} className="text-slate-700 italic leading-relaxed">{q}</p>
                        ))}
                    </div>
                    {/* Real support */}
                    <div className="bg-slate-900 text-white rounded-2xl p-6 shadow-xl">
                        <div className="flex items-center gap-2 text-slate-300 font-black uppercase tracking-widest text-[11px] mb-4">
                            <LifeBuoy size={14} /> If you need to talk to someone
                        </div>
                        <div className="space-y-4">
                            {SUPPORT_LINES.map(s => {
                                const Icon = s.icon;
                                return (
                                    <div key={s.id} className="flex items-start gap-4">
                                        <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                                            <Icon size={18} className="text-white" />
                                        </div>
                                        <div>
                                            <p className="font-bold text-white leading-tight">{s.name}</p>
                                            <p className="text-sm text-slate-200">
                                                {s.detailParts.map((part, i) => part.href ? (
                                                    <a
                                                        key={i}
                                                        href={part.href}
                                                        className="font-semibold text-white underline decoration-slate-500 underline-offset-2 hover:decoration-white transition-colors"
                                                    >
                                                        {part.text}
                                                    </a>
                                                ) : (
                                                    <span key={i}>{part.text}</span>
                                                ))}
                                            </p>
                                            <p className="text-xs text-slate-400 mt-0.5">{s.note}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

        </div>
    );
};

export default TeenHubView;
